/*
  FTD entrance overlay.

  The overlay renders visible and is hidden before first paint by the boot script in
  theme.liquid, so this file never has to reveal anything. It handles the exit
  animation, focus containment and the session flag only.
*/
(() => {
  const entrance = document.querySelector('[data-ftd-entrance]');
  if (!entrance) return;

  const SESSION_KEY = 'ftd-entrance-seen';
  const EXIT_DURATION = 660;
  const root = document.documentElement;
  const enterButton = entrance.querySelector('[data-ftd-enter]');
  const inDesignMode = Boolean(window.Shopify && window.Shopify.designMode);

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const readSeen = () => {
    try {
      return window.sessionStorage.getItem(SESSION_KEY) === 'true';
    } catch (error) {
      return false;
    }
  };

  const writeSeen = () => {
    try {
      window.sessionStorage.setItem(SESSION_KEY, 'true');
    } catch (error) {
      /* The entrance still closes when session storage is unavailable. */
    }
  };

  /* Always leave the overlay in place in the theme editor, where the merchant needs to
     see and edit it on every reload. */
  if (readSeen() && !inDesignMode) {
    root.classList.remove('ftd-entrance-armed');
    entrance.remove();
    return;
  }

  root.classList.add('ftd-entrance-armed');

  /*
    Everything outside the overlay is marked inert while the gate is open. This contains
    keyboard focus and hides the page behind it from assistive technology. The previous
    approach (preventDefault on every Tab) trapped sighted keyboard users but left the
    homepage underneath fully readable and navigable by screen readers.
  */
  const inertedElements = [];

  const applyInert = () => {
    let node = entrance;
    while (node && node !== document.body) {
      const parent = node.parentElement;
      if (!parent) break;
      for (const sibling of parent.children) {
        if (sibling !== node && !sibling.inert) {
          sibling.inert = true;
          inertedElements.push(sibling);
        }
      }
      node = parent;
    }
  };

  const releaseInert = () => {
    while (inertedElements.length) {
      inertedElements.pop().inert = false;
    }
  };

  let isClosing = false;

  const closeEntrance = () => {
    if (isClosing) return;
    isClosing = true;

    writeSeen();
    releaseInert();

    entrance.classList.remove('is-entering');
    entrance.classList.add('is-exiting');
    root.classList.remove('ftd-entrance-armed');

    const finish = () => {
      entrance.classList.remove('is-exiting');
      entrance.classList.add('is-closed');

      const mainHeading = document.querySelector('#MainContent h1, #MainContent h2');
      if (mainHeading) {
        mainHeading.setAttribute('tabindex', '-1');
        mainHeading.focus({ preventScroll: true });
      }
    };

    if (prefersReducedMotion()) {
      finish();
    } else {
      window.setTimeout(finish, EXIT_DURATION);
    }
  };

  applyInert();

  if (enterButton) {
    enterButton.addEventListener('click', closeEntrance);
    window.requestAnimationFrame(() => enterButton.focus({ preventScroll: true }));
  }

  entrance.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeEntrance();
      return;
    }

    /* Fallback containment for browsers without inert. The overlay has a single
       control, so holding focus on it is correct behaviour either way. */
    if (event.key === 'Tab' && enterButton && !('inert' in HTMLElement.prototype)) {
      event.preventDefault();
      enterButton.focus();
    }
  });
})();
